import { Copy, Download, Printer } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { EnrollmentResult, TotpEnrollment } from "@/auth/types";
import { LoadingState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";

import { AuthField, FormAlert, useAuthErrorText } from "./components";

interface EnrollStepProps {
  start: () => Promise<TotpEnrollment>;
  confirm: (code: string) => Promise<EnrollmentResult>;
  /** Called after the backup codes were downloaded and confirmed. */
  onDone: (result: EnrollmentResult) => void;
}

/** The QR comes as SVG markup from identity-service — shown as an image, never injected as HTML. */
function svgDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function recoveryCodesFile(
  heading: string,
  codes: readonly string[],
  issuedAt: Date,
): string {
  return [
    heading,
    issuedAt.toISOString(),
    "",
    ...codes.map((code, i) => `${String(i + 1).padStart(2, " ")}. ${code}`),
    "",
  ].join("\r\n");
}

function download(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

/**
 * First-time 2-step setup (T11-02): 3 explained steps (install app → scan QR / type key → type
 * the code), then the backup codes, which must be downloaded before continuing — they are shown
 * only once. Owners/managers cannot leave this screen until it is finished (login is blocked).
 */
export function EnrollStep({ start, confirm, onDone }: EnrollStepProps) {
  const { t } = useTranslation("auth");
  const errorText = useAuthErrorText();
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EnrollmentResult | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    start().then(
      (value) => {
        if (alive) {
          setEnrollment(value);
        }
      },
      (err: unknown) => {
        if (alive) {
          setLoadError(errorText(err));
        }
      },
    );
    return () => {
      alive = false;
    };
    // `start` is stable per step; retry via `attempt`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = code.replace(/\D/g, "");
    if (value.length !== 6 || pending) {
      setError(t("errors.code_invalid"));
      return;
    }
    setPending(true);
    setError(null);
    try {
      setResult(await confirm(value));
    } catch (err: unknown) {
      setError(errorText(err));
      setCode("");
    } finally {
      setPending(false);
    }
  };

  if (result) {
    const file = recoveryCodesFile(t("setup.file_heading"), result.recoveryCodes, new Date());
    return (
      <section className="flex flex-col gap-4" data-step="recovery-codes">
        <h2 className="text-lg font-bold">{t("setup.codes_title")}</h2>
        <p className="text-sm">{t("setup.codes_intro")}</p>
        <ol
          className="grid grid-cols-2 gap-2 rounded-md border bg-muted p-3 font-mono text-sm"
          aria-label={t("setup.codes_title")}
        >
          {result.recoveryCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ol>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              download("shop-admin-backup-codes.txt", file);
              setDownloaded(true);
            }}
          >
            <Download className="size-4" aria-hidden />
            {t("setup.download")}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              window.print();
            }}
          >
            <Printer className="size-4" aria-hidden />
            {t("setup.print")}
          </Button>
        </div>
        <p role="status" className="text-sm font-medium">
          {downloaded ? t("setup.downloaded") : t("setup.must_download")}
        </p>
        <label className="flex min-h-touch items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-5"
            checked={saved}
            disabled={!downloaded}
            onChange={(event) => {
              setSaved(event.target.checked);
            }}
          />
          {t("setup.confirm_saved")}
        </label>
        <Button
          size="lg"
          disabled={!downloaded || !saved}
          onClick={() => {
            onDone(result);
          }}
        >
          {t("setup.continue")}
        </Button>
      </section>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-3">
        <FormAlert message={loadError} />
        <Button
          variant="outline"
          onClick={() => {
            setLoadError(null);
            setAttempt((n) => n + 1);
          }}
        >
          {t("states.retry", { ns: "common" })}
        </Button>
      </div>
    );
  }
  if (!enrollment) {
    return <LoadingState label={t("setup.loading")} />;
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-col gap-5"
      noValidate
      data-step="enroll"
    >
      <p className="text-sm">{t("setup.intro")}</p>
      <section className="flex flex-col gap-1">
        <h2 className="font-semibold">{t("setup.step1_title")}</h2>
        <p className="text-sm text-muted-foreground">{t("setup.step1_body")}</p>
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">{t("setup.step2_title")}</h2>
        <p className="text-sm text-muted-foreground">{t("setup.step2_body")}</p>
        <img
          src={svgDataUri(enrollment.qrSvg)}
          alt={t("setup.qr_alt")}
          className="size-48 self-center rounded border bg-white p-2"
          data-slot="totp-qr"
        />
        <p className="text-sm">{t("setup.manual")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <code
            className="rounded bg-muted px-2 py-1 font-mono text-sm break-all"
            data-slot="totp-secret"
          >
            {enrollment.secret}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void navigator.clipboard?.writeText(enrollment.secret).then(() => {
                setCopied(true);
              });
            }}
          >
            <Copy className="size-4" aria-hidden />
            {copied ? t("setup.copied") : t("setup.copy_secret")}
          </Button>
        </div>
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">{t("setup.step3_title")}</h2>
        <AuthField
          label={t("setup.code")}
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          error={error}
          className="text-center font-mono text-2xl tracking-[0.4em]"
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
          }}
        />
      </section>
      <Button type="submit" size="lg" disabled={pending}>
        {t("setup.submit")}
      </Button>
    </form>
  );
}
